import { useEffect, useState } from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import { toast } from "@/src/common/snackbar"
import CenteredModal from "@/src/common/centered-modal/CenteredModal"
import { Iconify } from "@/src/common/iconify"
import {
    assignRecruitersToJob,
    getJobAssignees,
    getWorkspaceTeamRecruiters,
    type InternalTeamMember,
} from "@/src/services/internal"

import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Stack from "@mui/material/Stack"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import IconButton from "@mui/material/IconButton"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Autocomplete from "@mui/material/Autocomplete"
import CircularProgress from "@mui/material/CircularProgress"
import { LoadingButton } from "@mui/lab"

export type AssignRecruitersToJobModalProps = {
    open: boolean
    onClose: () => void
    jobId?: string
    jobName: string
    workspaceId?: string
    onSuccess?: () => void
}

export function AssignRecruitersToJobModal({
    open,
    onClose,
    jobId,
    jobName,
    workspaceId,
    onSuccess,
}: AssignRecruitersToJobModalProps) {
    const [selectedRecruiters, setSelectedRecruiters] = useState<InternalTeamMember[]>([])

    const recruitersQuery = useQuery({
        queryKey: ["internal", "team-recruiters", workspaceId],
        queryFn: () => getWorkspaceTeamRecruiters(workspaceId as string),
        enabled: open && Boolean(workspaceId),
    })

    const assigneesQuery = useQuery({
        queryKey: ["internal", "job-assignees", jobId],
        queryFn: () => getJobAssignees(jobId as string),
        enabled: open && Boolean(jobId),
    })

    const recruiters = recruitersQuery.data?.data ?? []
    const isLoadingOptions = recruitersQuery.isLoading || assigneesQuery.isLoading
    const failedToLoad = recruitersQuery.isError || assigneesQuery.isError

    useEffect(() => {
        if (!open) {
            setSelectedRecruiters([])
            return
        }
        if (recruitersQuery.isLoading || assigneesQuery.isLoading) {
            return
        }
        const assigneeIds = new Set((assigneesQuery.data?.data ?? []).map((assignee) => assignee.id))
        setSelectedRecruiters((recruitersQuery.data?.data ?? []).filter((recruiter) => assigneeIds.has(recruiter.userId)))
    }, [open, recruitersQuery.isLoading, assigneesQuery.isLoading, assigneesQuery.data, recruitersQuery.data])

    const assignmentMutation = useMutation({
        mutationFn: () =>
            assignRecruitersToJob(jobId as string, {
                recruiterIds: selectedRecruiters.map((recruiter) => recruiter.userId),
            }),
        onSuccess: (apiResponse) => {
            const assignedCount = apiResponse.data.assigned
            toast.success(
                assignedCount === 0
                    ? "All recruiters unassigned from this job"
                    : `${assignedCount} recruiter${assignedCount === 1 ? "" : "s"} assigned successfully`
            )
            onSuccess?.()
            closeModal()
        },
    })

    const closeModal = () => {
        setSelectedRecruiters([])
        onClose()
    }

    const submitAssignment = () => {
        if (!jobId || !workspaceId) {
            return
        }
        assignmentMutation.mutate()
    }

    return (
        <CenteredModal open={open} onClose={closeModal} width={560}>
            <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" fontWeight={700}>
                        Assign recruiters
                    </Typography>
                    <IconButton onClick={closeModal} size="small" aria-label="Close">
                        <Iconify icon="eva:close-fill" width={24} />
                    </IconButton>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                    Choose who should see <strong>{jobName || "this job"}</strong> on their dashboard. Saving replaces
                    the current list.
                </Typography>

                {!workspaceId && (
                    <Alert severity="warning">This job has no workspace, so recruiters cannot be loaded.</Alert>
                )}

                {failedToLoad && <Alert severity="error">Recruiters could not be loaded. Please try again.</Alert>}

                {isLoadingOptions ? (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            Loading recruiters...
                        </Typography>
                    </Stack>
                ) : (
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={recruiters}
                        value={selectedRecruiters}
                        onChange={(_changeEvent, recruiterList) => setSelectedRecruiters(recruiterList)}
                        isOptionEqualToValue={(recruiterOption, recruiterValue) =>
                            recruiterOption.userId === recruiterValue.userId
                        }
                        getOptionLabel={(recruiter) => recruiter.fullName}
                        noOptionsText="No recruiters found in this workspace"
                        renderTags={(recruiterList, getTagProperties) =>
                            recruiterList.map((recruiter, recruiterIndex) => (
                                <Chip
                                    {...getTagProperties({ index: recruiterIndex })}
                                    key={recruiter.userId}
                                    label={recruiter.fullName}
                                    size="small"
                                />
                            ))
                        }
                        renderOption={(optionProperties, recruiter, optionState) => (
                            <Box component="li" {...optionProperties} key={recruiter.userId}>
                                <Checkbox checked={optionState.selected} sx={{ mr: 1 }} />
                                <Stack>
                                    <Typography variant="body2" fontWeight={600}>
                                        {recruiter.fullName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {recruiter.email}
                                    </Typography>
                                </Stack>
                            </Box>
                        )}
                        renderInput={(inputParameters) => (
                            <TextField
                                {...inputParameters}
                                label="Recruiters"
                                placeholder={selectedRecruiters.length === 0 ? "Search and select recruiters" : ""}
                            />
                        )}
                    />
                )}

                <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ pt: 1 }}>
                    <Button variant="outlined" color="inherit" onClick={closeModal} disabled={assignmentMutation.isPending}>
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        loading={assignmentMutation.isPending}
                        disabled={!jobId || !workspaceId || isLoadingOptions || failedToLoad}
                        startIcon={<Iconify icon="mdi:account-plus-outline" />}
                        onClick={submitAssignment}
                    >
                        Save assignment
                    </LoadingButton>
                </Stack>
            </Stack>
        </CenteredModal>
    )
}
